import { DefaultButton, IconButton, Stack } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useRef, useState } from "react";
import { store } from "../../store";

interface JSONFileReaderProps<T> {
    validate: (data: object) => boolean;
    onJsonParsed: (data: T) => void;
    onClear: () => void;
    disabled?: boolean;
}

export function JSONFileReader<T>({
    validate,
    onJsonParsed,
    onClear,
    disabled,
}: JSONFileReaderProps<T>): JSX.Element {
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>();
    const [clearable, { setTrue: setClearable, setFalse: unsetClearable }] = useBoolean(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        if (file.type !== "application/json") {
            setError(store.i18n.t("component.jsonFileReader.invalidFileExtension"));
            return;
        }

        setFileName(file.name);
        const reader = new FileReader();

        reader.onload = (event: ProgressEvent<FileReader>) => {
            try {
                const text = event.target?.result as string;
                const parsed = JSON.parse(text);

                if (validate(parsed)) {
                    onJsonParsed(parsed);
                    setError(null);
                } else {
                    setError(store.i18n.t("component.jsonFileReader.invalidStructure"));
                }
                setClearable();
            } catch {
                setError(store.i18n.t("component.jsonFileReader.invalidJson"));
            }
        };

        reader.readAsText(file);
    };

    const handleButtonClick = (): void => {
        inputRef.current?.click();
    };

    return (
        <Stack>
            <Stack horizontal verticalAlign="center">
                <Stack.Item style={{ width: "100%" }}>
                    <DefaultButton onClick={handleButtonClick} disabled={disabled}>
                        {fileName ?? store.i18n.t("component.jsonFileReader.upload")}
                    </DefaultButton>
                </Stack.Item>
                <Stack.Item align="end">
                    <IconButton
                        disabled={!clearable}
                        iconProps={{ iconName: "Cancel" }}
                        onClick={() => {
                            setFileName(undefined);
                            setError(null);
                            onClear();
                            unsetClearable();
                        }}
                    />
                </Stack.Item>
            </Stack>
            {error && <p className="mt-2 text-red-500">{error}</p>}
            <input
                type="file"
                accept=".json"
                ref={inputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </Stack>
    );
}
